import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { ColDef } from 'ag-grid-community'
import DataGrid from '@/components/data/DataGrid'
import Tag from '@/components/ui/Tag'
import Button from '@/components/ui/Button'
import { FileText } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { formatDateForAPI } from '@/components/ui/DateRangePicker/utils'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import { TaskService } from '@/api/generated/services/TaskService'
import type { TaskFullResponse } from '@/api/generated/models/TaskFullResponse'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR'

interface TasksTableProps {
  workspaceId: string
  selectedBusinessId?: string
  selectedDepartmentId?: string
  selectedMemberId?: string
  isVisible: boolean
  startDate?: Date | null
  endDate?: Date | null
  searchQuery?: string
  searchTrigger?: number  // Explicit search trigger
  onTaskClick?: (task: TaskTreeNode) => void
  onReportClick?: (task: TaskTreeNode) => void
  isActionButtonClicked?: boolean
  refreshTrigger?: number
  onDataRefreshed?: (tasks: TaskFullResponse[]) => void
  status?: string  // Status filter: 'incomplete' | 'completed'
  performanceStatus?: string  // 성과상태 filter: 'incomplete' | 'completed' (status 와 AND)
}

const TasksTable: React.FC<TasksTableProps> = ({
  workspaceId,
  selectedBusinessId,
  selectedDepartmentId,
  selectedMemberId,
  isVisible,
  startDate,
  endDate,
  searchQuery,
  searchTrigger,
  onTaskClick,
  onReportClick,
  isActionButtonClicked,
  refreshTrigger,
  onDataRefreshed,
  status,
  performanceStatus
}) => {
  // Local state for task data management
  const [tasks, setTasks] = useState<TaskFullResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set())

  // TaskFullResponse to TaskTreeNode conversion helper
  const convertTaskToTreeNode = useCallback((task: TaskFullResponse): TaskTreeNode => {
    return {
      id: task.id,
      type: 'task',
      name: task.name,
      business_name: task.business_name || '',
      department_name: task.department_name || '',
      assignee: task.assignee as any,
      sub_assignee: task.sub_assignee as any,
      manager: task.manager as any,
      reviewer: task.reviewer as any,
      // Populate orgHierarchy for hierarchy display
      orgHierarchy: [
        task.business_name || '',
        task.task_group_name || '',
        task.task_subgroup_name || ''
      ].filter(Boolean),
      originalData: {
        id: task.id,
        type: 'task',
        name: task.name,
        task_subgroup_id: task.task_subgroup_id,
        start_at: task.start_at || '',
        end_at: task.end_at || '',
        status: task.status,
        department_id: task.department_id,
        department_name: task.department_name || '',
        assignee: task.assignee as any,
        sub_assignee: task.sub_assignee as any,
        manager: task.manager as any,
        reviewer: task.reviewer as any,
        budget: task.budget as any,
        performance: task.performance as any,
        task_items: task.task_items as any,
        // Add hierarchy fields for TaskDetailPanel
        business_name: task.business_name,
        business_id: task.business_id,
        task_group_name: task.task_group_name,
        task_subgroup_name: task.task_subgroup_name,
        // Add labels, detail_infos, extra_infos for TaskDetailPanel display
        labels: task.labels || [],
        detail_infos: task.detail_infos || [],
        extra_infos: task.extra_infos || []
      } as any
    }
  }, [])


  // Fetch tasks data
  const fetchTasks = useCallback(async (page: number, reset: boolean = false) => {
    if (!workspaceId) return


    try {
      if (reset) {
        setIsLoading(true)
        setTasks([])
        setLoadedPages(new Set())
        setError(null)
      } else {
        setIsLoadingMore(true)
      }

      const params: any = {
        workspaceId,
        page,
        perPage: pageSize
      }

      // Add date range parameters if provided
      if (startDate) {
        params.startDate = formatDateForAPI(startDate)
      }
      if (endDate) {
        params.endDate = formatDateForAPI(endDate)
      }

      // Add search query if provided
      if (searchQuery) {
        params.q = searchQuery
      }

      // Add filters if selected (exclude if "전체" is selected)
      if (selectedBusinessId) {
        params.businessId = selectedBusinessId
      }
      if (selectedDepartmentId) {
        params.departmentId = selectedDepartmentId
      }
      if (selectedMemberId) {
        // Multi-role filtering: pass same member ID to all role parameters
        params.stakeholderId = selectedMemberId
      }
      if (status) {
        params.status = status
      }
      if (performanceStatus) {
        params.performanceStatus = performanceStatus
      }

      const response = await TaskService.appsTaskViewsListTasks(params)


      if (response && response.data) {
        const newTasks = response.data
        const total = response.meta?.total || 0

        if (reset) {
          // 🔑 Sparse array 생성 (문서의 147-201 라인 패턴)
          const sparseArray: any[] = []
          for (let i = 0; i < total; i++) {
            if (i < newTasks.length) {
              // 첫 페이지는 실제 데이터
              sparseArray.push(newTasks[i])
            } else {
              // 나머지는 플레이스홀더
              sparseArray.push({
                id: `placeholder-task-${i}`,
                isPlaceholder: true
              })
            }
          }
          setTasks(sparseArray)
        } else {
          // 🔑 Sparse loading: 정확한 위치에 데이터 삽입 (문서의 243-261 라인 패턴)
          const startIndex = (page - 1) * pageSize

          setTasks(prev => {
            const updated = [...prev]

            // 배열 크기 보장
            const requiredLength = Math.max(updated.length, total)
            while (updated.length < requiredLength) {
              updated.push({
                id: `placeholder-task-${updated.length}`,
                isPlaceholder: true
              } as any)
            }

            // 새 페이지 데이터를 정확한 위치에 삽입
            for (let i = 0; i < newTasks.length; i++) {
              updated[startIndex + i] = newTasks[i]
            }

            return updated
          })
        }

        setTotalItems(total)
        setHasNext(response.meta?.has_next || false)
        setLoadedPages(prev => new Set([...prev, page]))

        // Notify parent component of data refresh (for edit success updates)
        if (reset && onDataRefreshed) {
          onDataRefreshed(newTasks)
        }
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err)
      setError('세부실행과제 목록을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  // status/performanceStatus 가 빠지면 이전 필터값으로 조회된다 (드롭다운과 목록 불일치)
  }, [workspaceId, selectedBusinessId, selectedDepartmentId, selectedMemberId, startDate, endDate, searchQuery, status, performanceStatus, pageSize, onDataRefreshed])

  // Row click handler
  const handleRowClick = useCallback((event: any) => {
    if (isActionButtonClicked || !event.data || event.data.isPlaceholder) return

    if (onTaskClick) {
      const task = event.data as TaskFullResponse
      const treeNode = convertTaskToTreeNode(task)
      onTaskClick(treeNode)
    }
  }, [onTaskClick, convertTaskToTreeNode, isActionButtonClicked])

  // Initial data loading
  useEffect(() => {
    if (isVisible && workspaceId) {
      fetchTasks(1, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, workspaceId, selectedBusinessId, selectedDepartmentId, selectedMemberId, startDate, endDate, searchTrigger, pageSize, refreshTrigger, status, performanceStatus])

  // ❌ Virtual row data 제거 - fetchTasks에서 sparse array를 생성하므로 불필요
  // Sparse array는 tasks에 이미 포함되어 있음 (문서 868라인)

  // Column definitions (matching original DetailDashboard structure)
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '세부실행과제상태',
      field: 'status',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        // 플레이스홀더 행 처리
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded w-16'
          })
        }
        const status = params.data?.status
        if (!status) return '-'

        const isCompleted = status === 'completed'
        return React.createElement(Tag, {
          size: 'small',
          color: isCompleted ? 'success' : 'gray'
        }, isCompleted ? '완료' : '미완료')
      }
    },
    {
      headerName: '성과상태',
      field: 'performance.status',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        // 플레이스홀더 행 처리
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded w-16'
          })
        }
        const status = params.data?.performance?.status
        if (!status) return '-'

        const isCompleted = status === 'completed'
        return React.createElement(Tag, {
          size: 'small',
          color: isCompleted ? 'success' : 'gray'
        }, isCompleted ? '완료' : '미완료')
      }
    },
    {
      headerName: '세부실행과제명',
      field: 'name',
      flex: 2,
      sortable: false,
      filter: false,
      suppressSizeToFit: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded'
          })
        }
        return params.value || '-'
      }
    },
    {
      headerName: '액션',
      field: 'actions',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        // 플레이스홀더 행 처리
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-8 bg-gray-50 animate-pulse rounded w-24'
          })
        }

        const handleTaskReportClick = (e: React.MouseEvent) => {
          e.stopPropagation()

          if (params.data && !params.data.isPlaceholder) {
            // Convert task data to TaskTreeNode format
            const taskTreeNode: TaskTreeNode = {
              id: params.data.id,
              type: 'task',
              name: params.data.name,
              business_name: params.data.business_name || '',
              department_name: params.data.department_name,
              assignee: params.data.assignee,
              sub_assignee: params.data.sub_assignee,
              manager: params.data.manager,
              reviewer: params.data.reviewer,
              orgHierarchy: [],
              originalData: params.data
            }
            onReportClick && onReportClick(taskTreeNode)
          }
        }

        return React.createElement('div', {
          className: 'flex items-center justify-center h-full'
        }, React.createElement(Button, {
          size: 'small',
          color: 'light',
          onClick: handleTaskReportClick,
          className: 'flex items-center gap-1 px-2',
          title: '보고서 보기'
        }, [
          React.createElement(FileText, { key: 'icon', className: 'w-3 h-3' }),
          '보고서'
        ]))
      }
    },
    {
      headerName: '시작일',
      field: 'start_at',
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded'
          })
        }
        return params.data?.start_at || '-'
      }
    },
    {
      headerName: '종료일',
      field: 'end_at',
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded'
          })
        }
        return params.data?.end_at || '-'
      }
    },
    {
      headerName: '부서',
      field: 'department_name',
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded'
          })
        }
        return params.data?.department_name || '-'
      }
    },
    {
      headerName: '담당자',
      field: 'assignee.name',
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded'
          })
        }
        return params.data?.assignee?.name || '-'
      }
    },
    {
      headerName: '관리자',
      field: 'manager.name',
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded'
          })
        }
        return params.data?.manager?.name || '-'
      }
    },
    {
      headerName: '라벨',
      field: 'labels',
      width: 200,
      minWidth: 120,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return React.createElement('div', {
            className: 'h-6 bg-gray-50 animate-pulse rounded'
          })
        }

        const labels = params.data?.labels
        if (!labels || labels.length === 0) return null

        return React.createElement(
          'div',
          { className: 'flex items-center gap-1 flex-wrap' },
          labels.map((label: any, index: number) =>
            React.createElement(Tag, {
              key: label.id || index,
              size: 'small',
              color: 'gray'
            }, label.key)
          )
        )
      }
    }
  ], [])

  // Handle pagination change
  const handlePaginationChanged = useCallback((params: any) => {
    if (!workspaceId) return

    const api = params.api
    const currentPageNum = api.paginationGetCurrentPage() + 1
    const currentPageSize = api.paginationGetPageSize()

    // Handle page size change
    if (currentPageSize !== pageSize) {
      setPageSize(currentPageSize)
      fetchTasks(1, true)
      return
    }

    // Loading guard - prevent duplicate calls
    if (isLoadingMore) {
      return
    }

    // Sparse Loading pattern - check for placeholders in current page range
    const startIndex = (currentPageNum - 1) * pageSize
    const endIndex = startIndex + pageSize
    const actualEndIndex = Math.min(endIndex, totalItems)

    let needsLoad = false

    // If the current page range exceeds loaded data, we need to load
    if (actualEndIndex > tasks.length) {
      needsLoad = true
    } else {
      // Check if any item in current page range is a placeholder
      for (let i = startIndex; i < actualEndIndex; i++) {
        if (!tasks[i] || tasks[i].isPlaceholder) {
          needsLoad = true
          break
        }
      }
    }

    // Load the current page if needed and not already loaded
    if (needsLoad && !loadedPages.has(currentPageNum)) {
      fetchTasks(currentPageNum, false)
    }
  }, [workspaceId, pageSize, tasks, totalItems, isLoadingMore, loadedPages, fetchTasks])

  // Show error state only when there's an actual error
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div
          className="flex items-center justify-center h-32 text-center"
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-medium'][0],
            color: designTokens.colors.error.DEFAULT,
          }}
        >
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <DataGrid
        columnDefs={columnDefs}
        rowData={tasks}
        rowModelType="clientSide"
        pagination={true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={[20, 50, 100]}

        // Server total count display
        localeText={{
          ...AG_GRID_LOCALE_KR,
          paginationOf: `총 ${totalItems.toLocaleString()}개`,
          page: '페이지',
          to: '-',
          of: `중`
        }}

        // Pagination event handler
        onPaginationChanged={handlePaginationChanged}

        // Row ID configuration
        getRowId={(params: any) => {
          // Ensure we have a valid string ID
          return params.data ? String(params.data.id) : ''
        }}

        onRowClicked={handleRowClick}
        loading={isLoading || isLoadingMore}
        headerHeight={40}
        rowHeight={42}
        height={40 + (20 * 42) + 60 + 2} // header + (20 rows * rowHeight) + pagination height + border
        suppressMovableColumns={true}
        suppressMenuHide={true}
        animateRows={true}

        // 범위 선택 기능 활성화
        enableRangeSelection={true}
        rangeSelectionExcludeColumns={['actions']}
        enableCellCopy={true}
        suppressRowClickSelection={true}
        rangeCopyFormatter={(colId, value) => {
          // 상태 컬럼 포맷팅
          if (colId === 'status') {
            return value === 'completed' ? '완료' : '미완료'
          }
          // 담당자/관리자 컬럼 (nested path)
          if (colId === 'assignee.name' || colId === 'manager.name') {
            return value || '-'
          }
          // null/undefined 처리
          if (value === null || value === undefined) return '-'
          return value.toString()
        }}
      />
    </div>
  )
}

export default TasksTable